import React from 'react';
import { SEO } from '@/components/SEO';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Phone, MessageCircle } from 'lucide-react';
import {
  generalFaqs,
  pressureWashingFaqs,
  softWashFaqs,
  windowFaqs,
  gutterFaqs,
  solarFaqs,
  carpetFaqs,
  dumpsterPadFaqs,
  trustFaqs,
} from '@/data/faqs';

const FAQPage = () => {
  // Combine all FAQs for schema
  const allFaqs = [
    ...generalFaqs,
    ...pressureWashingFaqs,
    ...softWashFaqs,
    ...windowFaqs,
    ...gutterFaqs,
    ...solarFaqs,
    ...carpetFaqs,
    ...dumpsterPadFaqs,
    ...trustFaqs
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": allFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const FAQSection = ({ faqs }: { faqs: { question: string; answer: string }[] }) => (
    <Accordion type="single" collapsible className="w-full">
      {faqs.map((faq, index) => (
        <AccordionItem key={index} value={`item-${index}`}>
          <AccordionTrigger className="text-left text-lg font-medium">
            {faq.question}
          </AccordionTrigger>
          <AccordionContent className="text-gray-600 text-base leading-relaxed">
            {faq.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );

  return (
    <>
      <SEO
        title="FAQ | Power Washing Questions Answered - Ventura Power Washing"
        description="Find answers to common questions about power washing, window cleaning, gutter maintenance, and more in Ventura County. Pricing, scheduling, service areas, and eco-friendly methods explained."
        keywords="power washing FAQ Ventura, pressure washing questions, window cleaning cost Ventura County, gutter cleaning frequency, solar panel cleaning FAQ"
        path="/faq"
      >
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </SEO>

      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-ventura-blue to-ventura-blue/80 text-white py-16 md:py-20">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
              <p className="text-xl text-white/90">
                Find answers to common questions about our power washing, window cleaning, and property maintenance services in Ventura County.
              </p>
            </div>
          </div>
        </section>


        {/* FAQ Content */}
        <section className="section-padding bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Tabs defaultValue="general" className="w-full">
                <TabsList className="flex flex-wrap justify-center gap-2.5 mb-8 h-auto bg-transparent">
                  <TabsTrigger value="general" className="rounded-full px-5 data-[state=active]:bg-ventura-blue data-[state=active]:text-white data-[state=inactive]:border data-[state=inactive]:border-border data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:border-ventura-blue">General & Pricing</TabsTrigger>
                  <TabsTrigger value="pressure" className="rounded-full px-5 data-[state=active]:bg-ventura-blue data-[state=active]:text-white data-[state=inactive]:border data-[state=inactive]:border-border data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:border-ventura-blue">Pressure Washing</TabsTrigger>
                  <TabsTrigger value="soft" className="rounded-full px-5 data-[state=active]:bg-ventura-blue data-[state=active]:text-white data-[state=inactive]:border data-[state=inactive]:border-border data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:border-ventura-blue">Soft Washing</TabsTrigger>
                  <TabsTrigger value="windows" className="rounded-full px-5 data-[state=active]:bg-ventura-blue data-[state=active]:text-white data-[state=inactive]:border data-[state=inactive]:border-border data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:border-ventura-blue">Windows</TabsTrigger>
                  <TabsTrigger value="gutters" className="rounded-full px-5 data-[state=active]:bg-ventura-blue data-[state=active]:text-white data-[state=inactive]:border data-[state=inactive]:border-border data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:border-ventura-blue">Gutters</TabsTrigger>
                  <TabsTrigger value="solar" className="rounded-full px-5 data-[state=active]:bg-ventura-blue data-[state=active]:text-white data-[state=inactive]:border data-[state=inactive]:border-border data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:border-ventura-blue">Solar Panels</TabsTrigger>
                  <TabsTrigger value="carpet" className="rounded-full px-5 data-[state=active]:bg-ventura-blue data-[state=active]:text-white data-[state=inactive]:border data-[state=inactive]:border-border data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:border-ventura-blue">Carpets</TabsTrigger>
                  <TabsTrigger value="dumpster" className="rounded-full px-5 data-[state=active]:bg-ventura-blue data-[state=active]:text-white data-[state=inactive]:border data-[state=inactive]:border-border data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:border-ventura-blue">Dumpster Pads</TabsTrigger>
                  <TabsTrigger value="trust" className="rounded-full px-5 data-[state=active]:bg-ventura-blue data-[state=active]:text-white data-[state=inactive]:border data-[state=inactive]:border-border data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:border-ventura-blue">About Us</TabsTrigger>
                </TabsList>

                <TabsContent value="general" forceMount className="data-[state=inactive]:hidden">
                  <h2 className="text-2xl font-bold mb-6">General Questions & Pricing</h2>
                  <FAQSection faqs={generalFaqs} />
                </TabsContent>

                <TabsContent value="pressure" forceMount className="data-[state=inactive]:hidden">
                  <h2 className="text-2xl font-bold mb-6">Pressure Washing FAQs</h2>
                  <FAQSection faqs={pressureWashingFaqs} />
                  <div className="mt-6">
                    <Link to="/services/pressure-washing" className="text-ventura-blue hover:underline font-medium">
                      Learn more about our pressure washing services →
                    </Link>
                  </div>
                </TabsContent>

                <TabsContent value="soft" forceMount className="data-[state=inactive]:hidden">
                  <h2 className="text-2xl font-bold mb-6">Soft Washing FAQs</h2>
                  <FAQSection faqs={softWashFaqs} />
                  <div className="mt-6">
                    <Link to="/services/soft-wash" className="text-ventura-blue hover:underline font-medium">
                      Learn more about our soft wash services →
                    </Link>
                  </div>
                </TabsContent>

                <TabsContent value="windows" forceMount className="data-[state=inactive]:hidden">
                  <h2 className="text-2xl font-bold mb-6">Window Cleaning FAQs</h2>
                  <FAQSection faqs={windowFaqs} />
                  <div className="mt-6">
                    <Link to="/services/window-washing" className="text-ventura-blue hover:underline font-medium">
                      Learn more about our window cleaning services →
                    </Link>
                  </div>
                </TabsContent>

                <TabsContent value="gutters" forceMount className="data-[state=inactive]:hidden">
                  <h2 className="text-2xl font-bold mb-6">Gutter Cleaning FAQs</h2>
                  <FAQSection faqs={gutterFaqs} />
                  <div className="mt-6">
                    <Link to="/services/gutter-cleaning" className="text-ventura-blue hover:underline font-medium">
                      Learn more about our gutter cleaning services →
                    </Link>
                  </div>
                </TabsContent>

                <TabsContent value="solar" forceMount className="data-[state=inactive]:hidden">
                  <h2 className="text-2xl font-bold mb-6">Solar Panel Cleaning FAQs</h2>
                  <FAQSection faqs={solarFaqs} />
                  <div className="mt-6">
                    <Link to="/services/solar-panel-cleaning" className="text-ventura-blue hover:underline font-medium">
                      Learn more about our solar panel cleaning services →
                    </Link>
                  </div>
                </TabsContent>

                <TabsContent value="carpet" forceMount className="data-[state=inactive]:hidden">
                  <h2 className="text-2xl font-bold mb-6">Carpet Cleaning FAQs</h2>
                  <FAQSection faqs={carpetFaqs} />
                  <div className="mt-6">
                    <Link to="/services/carpet-cleaning" className="text-ventura-blue hover:underline font-medium">
                      Learn more about our carpet cleaning services →
                    </Link>
                  </div>
                </TabsContent>

                <TabsContent value="dumpster" forceMount className="data-[state=inactive]:hidden">
                  <h2 className="text-2xl font-bold mb-6">Dumpster Pad Cleaning FAQs</h2>
                  <FAQSection faqs={dumpsterPadFaqs} />
                  <div className="mt-6">
                    <Link to="/services/dumpster-pad-cleaning" className="text-ventura-blue hover:underline font-medium">
                      Learn more about our dumpster pad cleaning services →
                    </Link>
                  </div>
                </TabsContent>

                <TabsContent value="trust" forceMount className="data-[state=inactive]:hidden">
                  <h2 className="text-2xl font-bold mb-6">About Ventura Power Washing</h2>
                  <FAQSection faqs={trustFaqs} />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-gray-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
              <p className="text-lg text-gray-600 mb-8">
                Can't find the answer you're looking for? Our friendly team is here to help with any questions about our services in Ventura County.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  className="button-primary flex items-center gap-2"
                  onClick={() => window.location.href = "tel:+18054647166"}
                >
                  <Phone size={18} />
                  Call (805) 464-7166
                </Button>
                <Link to="/free-estimate">
                  <Button variant="outline" className="flex items-center gap-2 w-full">
                    <MessageCircle size={18} />
                    Get a Free Estimate
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default FAQPage;